'use client';

import { useEffect, useState } from 'react';

import { contrastingTextColorFromRgb } from '../utils/color';

export interface ImageDominantColor {
  /** `#rrggbb` of the image's DOMINANT (most-common bucketed) color — null
   *  before load or on CORS failure. */
  color: string | null;
  /** Contrasting text for `color` — `#000000` on light, `#ffffff` on dark. */
  contrastingText: string | null;
}

const EMPTY: ImageDominantColor = { color: null, contrastingText: null };

function rgbToHex(r: number, g: number, b: number): string {
  return `#${[r, g, b]
    .map(x => Math.max(0, Math.min(255, x)).toString(16).padStart(2, '0'))
    .join('')}`;
}

/**
 * Sample an image's DOMINANT color (client-only) — 1:1 port of legacy
 * `extractDominantColorWeb` (`utils/imageColorUtils.ts`): draw the image onto a
 * 50×50 canvas, bucket every opaque pixel to the nearest 10 per channel, and
 * return the average of the most-populated bucket. This is NOT the average pixel
 * ({@link useImageAverageColor}) — the average of a busy banner reads as muddy
 * grey, whereas the dominant bucket picks the prominent hue, matching legacy's
 * series/course card tint.
 *
 * The URL must be CORS-enabled (raw Firebase Storage URLs are); a tainted-canvas
 * read throws and the nulls persist so callers keep their defaults.
 */
export function useImageDominantColor(url?: string | null): ImageDominantColor {
  const [result, setResult] = useState<ImageDominantColor>(EMPTY);

  useEffect(() => {
    if (!url) {
      setResult(EMPTY);
      return;
    }

    let cancelled = false;
    const image = new window.Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      if (cancelled) return;
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const size = 50;
        canvas.width = size;
        canvas.height = size;
        ctx.drawImage(image, 0, 0, size, size);
        const data = ctx.getImageData(0, 0, size, size).data;

        const colorCounts: Record<string, number> = {};
        const colorSums: Record<
          string,
          { r: number; g: number; b: number; count: number }
        > = {};

        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          const a = data[i + 3];
          if (a < 128) continue; // skip transparent

          const bucket = `${Math.round(r / 10) * 10}_${
            Math.round(g / 10) * 10
          }_${Math.round(b / 10) * 10}`;
          if (!colorCounts[bucket]) {
            colorCounts[bucket] = 0;
            colorSums[bucket] = { r: 0, g: 0, b: 0, count: 0 };
          }
          colorCounts[bucket]++;
          colorSums[bucket].r += r;
          colorSums[bucket].g += g;
          colorSums[bucket].b += b;
          colorSums[bucket].count++;
        }

        let maxCount = 0;
        let dominant: [number, number, number] = [0, 0, 0];
        for (const bucket in colorCounts) {
          if (colorCounts[bucket] > maxCount) {
            maxCount = colorCounts[bucket];
            const avg = colorSums[bucket];
            dominant = [
              Math.round(avg.r / avg.count),
              Math.round(avg.g / avg.count),
              Math.round(avg.b / avg.count),
            ];
          }
        }

        setResult({
          color: rgbToHex(dominant[0], dominant[1], dominant[2]),
          contrastingText: contrastingTextColorFromRgb(dominant),
        });
      } catch {
        // Cross-origin-tainted canvas read — legacy `extractDominantColor`
        // falls back to BLACK (`fallbackColor = '#000000'`), NOT a light
        // neutral, so the card stays dark (with white text) rather than
        // flipping to a washed-out light grey. Mirror that fallback.
        setResult({ color: '#000000', contrastingText: '#ffffff' });
      }
    };
    // Image failed to load (network / CORS preflight) — same black fallback.
    image.onerror = () => {
      if (!cancelled) {
        setResult({ color: '#000000', contrastingText: '#ffffff' });
      }
    };
    image.src = url;

    return () => {
      cancelled = true;
    };
  }, [url]);

  return result;
}
